import { Spin, Tooltip } from "antd";
import { PERMISSIONS, ROLE, ROLE_CONFIG_KEYS } from "common/constants";
import APIConstants from "common/constants/APIConstants";
import { allPaymentPageRoutes } from "common/constants/AllRoutes";
import COLORS from "common/constants/Colors";
import { useAuthContext } from "common/contexts/AuthContext";
import AllOrders from "common/icons/AllOrdersGreen.svg";
import Configuration from "common/icons/ConfigurationGreen.svg";
import Customer from "common/icons/CustomerGreen.svg";
import Dashboard from "common/icons/DashBoardGreen.svg";
import Groups from "common/icons/Groups.png";
import Inventory from "common/icons/InventoryGreen.svg";
import Invoice from "common/icons/InvoicesGreen.svg";
import Ledger from "common/icons/Ledger.png";
import Logout from "common/icons/LogOutGreen.svg";
import PaymentsIcon from "common/icons/PaymentsGreen.svg";
import PlaceOrder from "common/icons/PlusWhite.svg";
import Representative from "common/icons/RepresentativeGreen.svg";
import Suppliers from "common/icons/SuppliersGreen.svg";
import AttendanceIcon from "common/icons/attendance.png";
import { checkPermission, getUserRole } from "common/utils";
import { api } from "common/utils/APIMethods";
import {
  PERMISSION_STRATEGIES,
  getPermissionStrategy
} from "common/utils/permissionStrategies";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { Link, useMatch, useNavigate, useResolvedPath } from "react-router-dom";
import styled, { css } from "styled-components";

const NavbarContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: ${COLORS.pale_cyan};
  color: ${COLORS.midnight_green};
  border-right: 1px solid rgba(0, 87, 84, 0.1);
  width: ${(props) => (props.open ? "180px" : "64px")};
  transition: width 0.3s ease;
`;

const EnneaButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 5vh;
  border-bottom: 1px solid rgba(0, 87, 84, 0.1);
  cursor: pointer;
  font-family: "Poppins";
  font-size: 1.2432rem;
  font-weight: 600;
`;

const LinksContainer = styled.div`
  height: 95vh;
  overflow: auto;
  padding-bottom: 90px;
  ::-webkit-scrollbar {
    width: 0;
  }
`;

const NavElement = styled.div`
  padding: 10px 20px;
  border-bottom: 1px solid rgba(0, 87, 84, 0.1);
  ${(props) =>
    props.match &&
    css`
      color: white;
      background-color: ${COLORS.wood_rush};
    `}
`;

const PlaceOrderElement = styled.div`
  width: 100%;
  height: 54px;
  display: flex;
  align-items: center;
  ${(props) => (props.match ? "padding-left: 0;" : "justify-content: center;")}
  background: ${(props) => (props.match ? COLORS.wood_rush : COLORS.pale_cyan)};
  border-bottom: 1px solid rgba(0, 87, 84, 0.1);
`;

const PlaceOrderLink = styled(Link)`
  display: flex;
  align-items: center;
  ${(props) =>
    !props.match
      ? `  
    height: 38px;
    padding: 0 10px;
    border-radius: 4px;
    border: 1px solid rgba(0, 148, 143, 0.3);
    background: linear-gradient(315deg, #00bfb9 0%, rgba(0, 191, 185, 0.65) 100%);
    box-shadow: 0px 4px 6px -3px rgba(0, 191, 185, 0.6);
    `
      : `
  padding-left: 20px;`}
  cursor: pointer;
`;

const LinkElement = styled(Link)`
  display: flex;
  align-items: center;
  color: ${(props) => (props.match ? "white" : COLORS.midnight_green)};
  :hover {
    color: inherit;
  }
`;

const Texts = styled.div`
  padding-left: 10px;
  font-family: Poppins;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  display: ${(props) => (props.open ? "flex" : "none")};
  white-space: nowrap;
`;

const PlaceOrderText = styled.div`
  display: ${(props) => (props.open ? "flex" : "none")};
  padding-left: 14px;
  color: #fff;
  font-family: Poppins;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  white-space: nowrap;
`;

const LogoutLogo = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  background-color: ${COLORS.pale_cyan};
  padding: 0 20px;
  height: 60px;
  border-top: 1px solid rgba(0, 87, 84, 0.1);
  border-right: 1px solid rgba(0, 87, 84, 0.1);
  cursor: pointer;
  position: fixed;
  bottom: 0;
  width: ${(props) => (props.open ? "180px" : "64px")};
`;

const LogoutText = styled.div`
  color: ${COLORS.midnight_green};
  font-family: Poppins;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  padding-left: 10px;
  display: ${(props) => (props.open ? "flex" : "none")};
`;

function NavbarLink({ open, to: forwardTo, image, text, parent = forwardTo }) {
  const resolved = useResolvedPath(parent);
  const match = useMatch({ path: resolved.pathname, end: false });
  const { t } = useTranslation();

  if (parent === "/placeorder") {
    return (
      <Tooltip
        title={!open ? t(text) : ""}
        trigger={["hover", "focus"]}
        placement="right"
      >
        <PlaceOrderElement open={open} match={match}>
          <PlaceOrderLink match={match} open={open} to={forwardTo}>
            <img height={20} width={20} src={image} alt="placeOrder" />
            <PlaceOrderText match={match} open={open}>
              {t(text)}
            </PlaceOrderText>
          </PlaceOrderLink>
        </PlaceOrderElement>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={!open ? t(text) : ""}
      trigger={["hover", "focus"]}
      placement="right"
    >
      <NavElement match={match}>
        <LinkElement open={open} match={match} to={forwardTo}>
          <img src={image} height={24} width={24} alt="logo" />
          <Texts open={open}>{t(text)}</Texts>
        </LinkElement>
      </NavElement>
    </Tooltip>
  );
}

const navBarConfig = [
  {
    component: {
      key: "placeOrder",
      parent: "/placeorder",
      to: "/placeorder/cfa",
      text: "place_order",
      image: PlaceOrder
    },
    permission: {
      config: ROLE_CONFIG_KEYS.cart,
      permission: PERMISSIONS.create
    }
  },
  {
    component: {
      key: "dashboard",
      to: "/dashboard",
      text: "dashboard",
      image: Dashboard
    },
    permission: {
      config: ROLE_CONFIG_KEYS.analytics,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      key: "orders",
      parent: "/orders",
      to: "/orders/list/default",
      text: "all_order",
      image: AllOrders
    },
    permission: {
      config: ROLE_CONFIG_KEYS.order,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      key: "inventory",
      parent: "/inventory",
      to: "/inventory",
      text: "inventory",
      image: Inventory
    },
    permission: {
      config: ROLE_CONFIG_KEYS.inventory,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      key: "payments",
      parent: "/payments",
      to: "/payments/outstanding",
      text: "payments",
      image: PaymentsIcon
    },
    permission: [
      {
        config: ROLE_CONFIG_KEYS.payment,
        permission: PERMISSIONS.read
      },
      {
        config: ROLE_CONFIG_KEYS.outstanding,
        permission: PERMISSIONS.read
      }
    ],
    strategy: PERMISSION_STRATEGIES.atLeastOneTrue,
    allRoutes: allPaymentPageRoutes
  },
  {
    component: {
      key: "invoices",
      parent: "/invoices",
      to: "/invoices/list",
      text: "invoices",
      image: Invoice
    },
    permission: {
      config: ROLE_CONFIG_KEYS.invoices,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      key: "expenses",
      parent: "/expenses",
      to: "/expenses/list",
      text: "expenses",
      image: Invoice
    },
    permission: {
      config: ROLE_CONFIG_KEYS.expense,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      key: "suppliers",
      parent: "/suppliers",
      to: "/suppliers",
      text: "suppliers",
      image: Suppliers
    },
    permission: {
      config: ROLE_CONFIG_KEYS.suppliers,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      key: "Groups",
      parent: "/groups",
      to: "/groups",
      text: "groups",
      image: Groups
    },
    permission: {
      config: ROLE_CONFIG_KEYS.groups,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      key: "customers",
      parent: "/customers",
      to: "/customers",
      text: "customers",
      image: Customer
    },
    permission: {
      config: ROLE_CONFIG_KEYS.customers,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      key: "ledgers",
      parent: "/ledgers",
      to: "/ledgers",
      text: "Ledgers",
      image: Ledger
    },
    permission: {
      config: ROLE_CONFIG_KEYS.customers,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      key: "representatives",
      parent: "/representatives",
      to: "/representatives",
      text: "representatives",
      image: Representative
    },
    permission: {
      config: ROLE_CONFIG_KEYS.representatives,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      key: "configuration",
      parent: "/configuration",
      to: "/configuration",
      text: "Configuration",
      image: Configuration
    },
    permission: {
      config: ROLE_CONFIG_KEYS.settings,
      permission: PERMISSIONS.read
    }
  }
];

const checkPermissionForNavItem = (
  permission,
  strategy = "simple",
  parsedRoleConfig = undefined
) => {
  const checkIfAuthorized = getPermissionStrategy(strategy);
  return checkIfAuthorized(parsedRoleConfig, permission);
};

const Navbar = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { dispatch, parsedRoleConfig, authConfig } = useAuthContext();
  const userRole = getUserRole(authConfig);
  const isRoleApprover = userRole === ROLE.approver;

  const logoutMutation = useMutation(
    () =>
      api(
        {
          url: APIConstants.logoutURL
        },
        authConfig
      ),
    {
      onSuccess: () => {
        dispatch({ type: "onLogout" });
        navigate("/login");
        window.location.reload();
        localStorage.clear();
      }
    }
  );

  const authorizedNavBarElements = useMemo(
    () =>
      navBarConfig
        .filter((navItem) =>
          checkPermissionForNavItem(
            navItem.permission,
            navItem.strategy,
            parsedRoleConfig
          )
        )
        .map((navItem) => {
          if (navItem.strategy === PERMISSION_STRATEGIES.atLeastOneTrue) {
            const forwardTo = navItem.allRoutes.find((route) =>
              checkPermission(parsedRoleConfig, route.permission)
            ).to;
            return {
              component: { ...navItem.component, to: forwardTo }
            };
          }
          return navItem;
        }),
    [parsedRoleConfig]
  );

  const onClickLogout = () => {
    logoutMutation.mutate();
  };

  const [navbarOpen, setNavbarOpen] = useState(true);
  const toggleNavbar = () => {
    setNavbarOpen(!navbarOpen);
  };

  return (
    <NavbarContainer open={navbarOpen}>
      <EnneaButton onClick={toggleNavbar}>
        {navbarOpen ? (
          <div>
            <span style={{ color: "#4e6199" }}>Value</span>
            <span style={{ color: "#1ac6bf" }}>Sale</span>
          </div>
        ) : (
          <div>
            <span style={{ color: "#4e6199" }}>V</span>
            <span style={{ color: "#1ac6bf" }}>S</span>
          </div>
        )}
      </EnneaButton>
      <LinksContainer>
        {authorizedNavBarElements.map((navItem) => (
          <NavbarLink open={navbarOpen} {...navItem.component} />
        ))}
        {false && (
          <Tooltip title={`${t("ledger_order")}`} trigger={["hover", "focus"]}>
            <NavbarLink
              open={navbarOpen}
              key="ledgerOrder"
              to="/ledgerOrder"
              text={`${t("ledger_order")}`}
              image={AllOrders}
            />
          </Tooltip>
        )}
        {isRoleApprover && (
          <NavbarLink
            open={navbarOpen}
            key="attendance"
            to="/attendance"
            text="Attendance"
            image={AttendanceIcon}
          />
        )}
        <Tooltip
          title={!navbarOpen ? t("logout") : ""}
          trigger={["hover", "focus"]}
        >
          <LogoutLogo open={navbarOpen} onClick={onClickLogout}>
            {logoutMutation.isLoading ? (
              <Spin />
            ) : (
              <img src={Logout} alt="logo" />
            )}
            <LogoutText open={navbarOpen}>{t("logout")}</LogoutText>
          </LogoutLogo>
        </Tooltip>
      </LinksContainer>
    </NavbarContainer>
  );
};

export default React.memo(Navbar);
